// apps/web/src/routes/signs/AnnotationCanvas.tsx
import { useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
// Use Vite's ?url import so the worker is bundled + served from our own origin.
// Works offline/intranet (no CDN dependency).
import pdfWorker from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import { AnnotationBox, type AnnotationBoxData } from "./AnnotationBox";

pdfjs.GlobalWorkerOptions.workerSrc = pdfWorker;

type Props = {
  fileUrl: string; // blob: or /api/file/:id/preview
  boxes: AnnotationBoxData[];
  signerOptions: { level: number; label: string }[];
  onAdd: (pageIndex: number, box: Omit<AnnotationBoxData, "tempId">) => void;
  onChangeBox: (box: AnnotationBoxData) => void;
  onDeleteBox: (tempId: string) => void;
};

const TARGET_WIDTH = 800; // CSS px; every page renders at this width

export function AnnotationCanvas({
  fileUrl,
  boxes,
  signerOptions,
  onAdd,
  onChangeBox,
  onDeleteBox,
}: Props) {
  const [numPages, setNumPages] = useState(0);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [pageDims, setPageDims] = useState<Record<number, { ptWidth: number; ptHeight: number }>>(
    {},
  );

  return (
    <Document
      file={fileUrl}
      onLoadSuccess={(doc) => setNumPages(doc.numPages)}
      // Không có onLoadError thì mọi nguyên nhân — worker chết, PDF hỏng, hết
      // bộ nhớ — đều hiện đúng một dòng "Không đọc được PDF", không tra được.
      // Ghi nguyên văn lỗi ra console để còn lần được.
      onLoadError={(err) => {
        console.error("[AnnotationCanvas] pdf.js không đọc được File ký:", err);
        setLoadError(err instanceof Error ? err.message : String(err));
      }}
      loading={<div className="p-8 text-center">Đang tải PDF…</div>}
      error={
        <div className="p-8 text-center text-destructive">
          Không đọc được PDF. Kiểm tra lại File ký.
          {loadError && <div className="mt-1 text-xs opacity-80">Chi tiết: {loadError}</div>}
        </div>
      }
    >
      {Array.from({ length: numPages }, (_, i) => i + 1).map((pageIndex) => (
        <PageSlot
          key={pageIndex}
          pageIndex={pageIndex}
          boxes={boxes.filter((b) => b.pageIndex === pageIndex)}
          signerOptions={signerOptions}
          onDims={(ptW, ptH) =>
            setPageDims((prev) => ({ ...prev, [pageIndex]: { ptWidth: ptW, ptHeight: ptH } }))
          }
          onAdd={(b) => onAdd(pageIndex, b)}
          onChangeBox={onChangeBox}
          onDeleteBox={onDeleteBox}
          dims={pageDims[pageIndex]}
        />
      ))}
    </Document>
  );
}

function PageSlot({
  pageIndex,
  boxes,
  signerOptions,
  dims,
  onAdd,
  onDims,
  onChangeBox,
  onDeleteBox,
}: {
  pageIndex: number;
  boxes: AnnotationBoxData[];
  signerOptions: { level: number; label: string }[];
  dims?: { ptWidth: number; ptHeight: number };
  onAdd: (b: Omit<AnnotationBoxData, "tempId">) => void;
  onDims: (w: number, h: number) => void;
  onChangeBox: (b: AnnotationBoxData) => void;
  onDeleteBox: (id: string) => void;
}) {
  const wrap = useRef<HTMLDivElement>(null);
  const scale = dims ? TARGET_WIDTH / dims.ptWidth : 1;
  const heightPx = dims ? dims.ptHeight * scale : 0;

  const handleAdd = () => {
    if (!dims) return;
    // Default box centered on page, 220pt × 70pt (matches OLD scaleToFit)
    const w = 220;
    const h = 70;
    const x = (dims.ptWidth - w) / 2;
    const y = (dims.ptHeight - h) / 2;
    onAdd({ signLevel: signerOptions[0]?.level ?? 1, pageIndex, x, y, w, h });
  };

  return (
    <div className="relative mx-auto mb-6" style={{ width: TARGET_WIDTH }}>
      <div className="flex items-center justify-between mb-1 text-xs text-muted-foreground">
        <span>Trang {pageIndex}</span>
        <button
          type="button"
          onClick={handleAdd}
          disabled={signerOptions.length === 0}
          className="px-2 py-1 rounded border border-primary-600 text-primary-600 hover:bg-primary-50 disabled:opacity-40"
        >
          + Thêm ô ký
        </button>
      </div>
      <div ref={wrap} className="relative border border-border shadow-sm">
        <Page
          pageNumber={pageIndex}
          width={TARGET_WIDTH}
          onLoadSuccess={(p) => onDims(p.originalWidth, p.originalHeight)}
          renderAnnotationLayer={false}
          renderTextLayer={false}
        />
        {dims &&
          boxes.map((b) => (
            <AnnotationBox
              key={b.tempId}
              box={b}
              pageHeightPx={heightPx}
              scale={scale}
              signerOptions={signerOptions}
              onChange={onChangeBox}
              onDelete={() => onDeleteBox(b.tempId)}
            />
          ))}
      </div>
    </div>
  );
}
